import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { useAppContext } from '../contexts/AppContext';
import { colors, spacing, typography } from '../theme';
import { useThemedStyles } from '../hooks/useThemedStyles';

export const FacebookCallbackScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const [isProcessing, setIsProcessing] = useState(true);
  const [status, setStatus] = useState('Processing Facebook authentication...');
  const { navigateTo } = useAppContext();

  useEffect(() => {
    handleFacebookCallback();
  }, []);

  const handleFacebookCallback = async () => {
    try {
      setStatus('Extracting authorization code...');
      
      // Get the current URL (for web) or extract from navigation state
      const currentUrl = window?.location?.href || '';
      console.log('🔍 [FacebookCallbackScreen] Current URL:', currentUrl);
      
      // Extract code from URL parameters
      const urlParams = new URLSearchParams(currentUrl.split('?')[1]);
      const code = urlParams.get('code');
      const error = urlParams.get('error');
      const errorDescription = urlParams.get('error_description');
      
      if (error) {
        throw new Error(errorDescription || 'Facebook authentication failed');
      }
      
      if (!code) {
        throw new Error('No authorization code received from Facebook');
      }
      
      console.log('✅ [FacebookCallbackScreen] Authorization code received');
      setStatus('Processing authentication with backend...');
      
      // Extract business ID from state parameter
      const stateParam = urlParams.get('state');
      let businessId = '';
      if (stateParam && stateParam.startsWith('facebook_auth_state_')) {
        businessId = stateParam.replace('facebook_auth_state_', '');
      }
      
      console.log('🔍 [FacebookCallbackScreen] Business ID:', businessId);
      
      // Call backend API to process the authentication
      const response = await fetch(`https://api-dev.sendifier.com/facebook-callback?code=${code}&state=${stateParam}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`Backend API call failed with status: ${response.status}`);
      }
      
      console.log('✅ [FacebookCallbackScreen] Backend processing successful');
      setStatus('Authentication successful! Redirecting...');
      
      // Close the popup/window and send message to parent
      if (window?.opener) {
        // We're in a popup, send success message to parent
        window.opener.postMessage({
          type: 'FACEBOOK_AUTH_SUCCESS',
          code: code
        }, window.location.origin);
        window.close();
      } else {
        // We're in the main window, redirect to Facebook Messenger setup
        setTimeout(() => {
          navigateTo('facebook-messenger-setup');
        }, 1000);
      }
      
    } catch (error: any) {
      console.error('❌ [FacebookCallbackScreen] Error:', error);
      setStatus('Authentication failed');
      
      // Send error message to parent if in popup
      if (window?.opener) {
        window.opener.postMessage({
          type: 'FACEBOOK_AUTH_ERROR',
          error: error.message
        }, window.location.origin);
        window.close();
      } else {
        // Show error and redirect
        Alert.alert(
          'Authentication Failed',
          error.message || 'Facebook authentication failed. Please try again.',
          [
            {
              text: 'OK',
              onPress: () => navigateTo('facebook-messenger-setup')
            }
          ]
        );
      }
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.content}>
        <ActivityIndicator 
          size="large" 
          color={colors.primary} 
          style={styles.spinner}
        />
        <Text style={styles.statusText}>{status}</Text>
        <Text style={styles.helpText}>
          Please wait while we complete your Facebook authentication...
        </Text>
      </View>
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.primary,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.lg,
  },
  content: {
    alignItems: 'center',
    maxWidth: 400,
  },
  spinner: {
    marginBottom: spacing.lg,
  },
  statusText: {
    fontSize: typography.fontSize.lg,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
    textAlign: 'center',
    marginBottom: spacing.md,
  },
  helpText: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
    textAlign: 'center',
    lineHeight: 20,
  },
});
